import {
  SUMMARY_ENTITY_ALL,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_PRODUCTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SEED_DRESS,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_AREA,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_TEMPORAL_MONTHLY,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput, IUnit } from 'interfaces/IInput';
import {
  type IInputStep,
  type IInputStepProduction,
  type IInputStepRateCost,
  type IInputStepSeed,
  type IInputStepSoilInput,
  InputStepType,
} from 'interfaces/IInputStep';
import type { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import { COST_STEP_TYPES } from 'pages/FarmsPage/Workspace/Insights/costCategories';
import {
  convertToHectares,
  convertToKg,
  convertToLitres,
  convertToTonnes,
  resolveUnitKind,
  type UnitKind,
} from 'utils/unitConversion';

import type {
  SummaryDataMetric,
  SummaryEntityCategory,
  SummaryTemporalLayout,
} from '../../../../redux/slices/InsightsSlice';

import { resolveAreaHa, resolveEffectiveRate } from './insightUtils';
import type { SummaryGridRow } from './SummaryGrid';

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

/** Columns in monthly mode: Total + No date + Jan–Dec. */
const MONTHLY_COLUMN_COUNT = 14;
/** Columns in flat mode: Total only. */
const FLAT_COLUMN_COUNT = 1;

/* ═══════════════════════════════════════════════════════════════
   Month bucketing
   ═══════════════════════════════════════════════════════════════ */

/**
 * Column index for a date in monthly mode.
 * 2–13 for Jan–Dec, 1 for "No date".
 */
const getMonthColumn = (dateStr: string | null): number => {
  if (!dateStr) return 1;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 1;
  return d.getUTCMonth() + 2;
};

/* ═══════════════════════════════════════════════════════════════
   Entity key resolution
   ═══════════════════════════════════════════════════════════════ */

/** True when globalId is a non-empty string (usable as a grouping key). */
const isValidGlobalId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && id.length > 0;

/**
 * Resolve the grouping key for a step-level entity.
 * Prefer globalId so that identical products (same globalId, possibly
 * different display names) merge into one row. Fall back to item name
 * when globalId is missing so that unnamed-but-same-name steps still
 * accumulate.
 */
const stepKey = (step: IInputStep): string | null => {
  if (isValidGlobalId(step.globalId)) return step.globalId;
  return step.item || null;
};

/**
 * Resolve effective crop/variety for an input.
 *
 * If Seed steps (301) exist, each one defines its own crop/variety.
 * Non-Seed steps use the FIRST Seed step's crop (so all costs
 * attribute to the seed crop, not input.crop).
 * If no Seed step exists, fall back to input.crop.
 */
interface ResolvedCrop {
  /** Crop name for non-Seed steps (first Seed step or input.crop). */
  crop: string;
  /** Variety name for non-Seed steps. */
  variety: string;
  /** True when at least one Seed step exists — input.crop is ignored. */
  hasSeed: boolean;
}

const resolveInputCrop = (input: IInput): ResolvedCrop => {
  for (const task of input.tasks) {
    for (const step of task.steps) {
      if (step.stepType === InputStepType.Seed) {
        const s = step as unknown as IInputStepSeed;
        return {
          crop: s.item || input.crop.cropName,
          variety: s.item2 ?? input.crop.variety?.varietyName ?? '',
          hasSeed: true,
        };
      }
    }
  }
  return {
    crop: input.crop.cropName,
    variety: input.crop.variety?.varietyName ?? '',
    hasSeed: false,
  };
};

/**
 * Row grouping key for a step based on entity category.
 * Returns `null` when the step should be excluded.
 */
const getEntityKey = (
  entityCategory: SummaryEntityCategory,
  step: IInputStep,
  resolved: ResolvedCrop
): string | null => {
  switch (entityCategory) {
    case SUMMARY_ENTITY_SEED: {
      if (step.stepType !== InputStepType.Seed) return null;
      const s = step as unknown as IInputStepSeed;
      return s.item2 ?? resolved.variety ?? '';
    }

    case SUMMARY_ENTITY_SOIL_INPUT:
      return step.stepType === InputStepType.SoilInput ? stepKey(step) : null;

    case SUMMARY_ENTITY_PLANT_PROTECTION:
      // Plant Protection covers both crop protection and adjuvant steps.
      return step.stepType === InputStepType.CropProtection ||
        step.stepType === InputStepType.Adjuvant
        ? stepKey(step)
        : null;

    case SUMMARY_ENTITY_BIOSTIMULANT:
      return step.stepType === InputStepType.Biostimulant
        ? stepKey(step)
        : null;

    case SUMMARY_ENTITY_SEED_DRESS:
      return step.stepType === InputStepType.SeedDress ? stepKey(step) : null;

    case SUMMARY_ENTITY_OPERATION:
      return step.stepType === InputStepType.Application ? stepKey(step) : null;

    case SUMMARY_ENTITY_PRODUCTION:
      return step.stepType === InputStepType.Production ? stepKey(step) : null;

    default:
      return null;
  }
};

/* ═══════════════════════════════════════════════════════════════
   Metric calculations
   ═══════════════════════════════════════════════════════════════ */

/**
 * Quantity = product volume, expressed in the product's own recorded unit
 * (rather than forced into tonnes) so the grid can show a meaningful unit
 * next to each row instead of a single blanket "Tonnes" label.
 *
 * Seed: Seed steps (301) → rate × area, unit = the step's own appUnit.
 * Others: rate-cost → rate × area, unit = appUnit;
 *   Production → yield × area, unit = yieldUnit.
 *
 * Returns `null` when the step doesn't contribute (zero/negative rate or
 * area, or no unit recorded — a bare number with no unit isn't meaningful).
 */
const calculateQuantityWithUnit = (
  entityCategory: SummaryEntityCategory,
  step: IInputStep,
  task: IInputTask,
  areaHa: number,
  gUnits: GUnit[]
): { value: number; unit: IUnit } | null => {
  // Seed quantity — getEntityKey already filters to Seed steps only.
  if (entityCategory === SUMMARY_ENTITY_SEED) {
    const s = step as unknown as IInputStepRateCost;
    const rate = s.rate ?? 0;
    if (rate <= 0 || areaHa <= 0 || !s.appUnit) return null;
    return { value: rate * areaHa, unit: s.appUnit };
  }

  // Rate-cost steps (including SeedDress, both rate modes)
  if (COST_STEP_TYPES.has(step.stepType)) {
    const s = step as unknown as IInputStepRateCost;
    const effectiveRate = resolveEffectiveRate(step, task, gUnits);
    if (
      effectiveRate === null ||
      effectiveRate <= 0 ||
      areaHa <= 0 ||
      !s.appUnit
    )
      return null;
    return { value: effectiveRate * areaHa, unit: s.appUnit };
  }

  // Production steps
  if (step.stepType === InputStepType.Production) {
    const s = step as IInputStepProduction;
    const raw = (s.yield ?? 0) * areaHa;
    if (raw <= 0 || !s.yieldUnit) return null;
    return { value: raw, unit: s.yieldUnit };
  }

  return null;
};

/**
 * Cost = rate × cost × area for cost steps.
 * Production steps: revenue = yield × price × area.
 */
const calculateCost = (
  step: IInputStep,
  task: IInputTask,
  areaHa: number,
  gUnits: GUnit[]
): number => {
  // Production step: revenue = yield × price × area
  if (step.stepType === InputStepType.Production) {
    const s = step as IInputStepProduction;
    const yld = s.yield ?? 0;
    const price = s.price ?? 0;
    if (yld <= 0 || price <= 0 || areaHa <= 0) return 0;
    return yld * price * areaHa;
  }

  if (!COST_STEP_TYPES.has(step.stepType)) return 0;
  const s = step as unknown as IInputStepRateCost;
  const cost = s.cost ?? 0;
  const effectiveRate = resolveEffectiveRate(step, task, gUnits);
  if (effectiveRate === null || effectiveRate <= 0 || cost <= 0 || areaHa <= 0)
    return 0;
  return effectiveRate * cost * areaHa;
};

/**
 * Dispatch to the correct metric calculator for Area/Cost. Quantity is
 * handled separately by the caller since it also needs to resolve and
 * reconcile a per-row unit — see `calculateQuantityWithUnit`.
 */
const calculateMetric = (
  dataMetric: SummaryDataMetric,
  step: IInputStep,
  task: IInputTask,
  areaHa: number,
  gUnits: GUnit[]
): number => {
  switch (dataMetric) {
    case SUMMARY_METRIC_TOTAL_AREA:
      return areaHa > 0 ? areaHa : 0;
    case SUMMARY_METRIC_TOTAL_COST:
      return calculateCost(step, task, areaHa, gUnits);
    default:
      return 0;
  }
};

/* ═══════════════════════════════════════════════════════════════
   Accumulator helpers
   ═══════════════════════════════════════════════════════════════ */

const getOrCreate = (
  map: Map<string, number[]>,
  key: string,
  columnCount: number
): number[] => {
  const existing = map.get(key);
  if (existing) return existing;
  const values = new Array<number>(columnCount).fill(0);
  map.set(key, values);
  return values;
};

const addValue = (
  values: number[],
  value: number,
  isMonthly: boolean,
  monthColumn: number
): void => {
  if (isMonthly) {
    values[monthColumn] += value;
    values[0] += value;
  } else {
    values[0] += value;
  }
};

/** Fixed display unit per kind — mass and liquid rows each converge to one shared unit across the whole table. */
const UNIT_KIND_LABEL: Record<UnitKind, string> = { mass: 'kg', liquid: 'L' };

const toSortedRows = (
  accumulator: Map<string, number[]>,
  groupMap: Map<string, string>,
  labelMap: Map<string, string>,
  countMap: Map<string, number>,
  kindMap: Map<string, UnitKind>
): SummaryGridRow[] =>
  Array.from(accumulator.entries())
    .map(([key, values]) => {
      const label = labelMap.get(key) ?? key;
      const group = groupMap.get(key);
      const mergeCount = countMap.get(key);
      const kind = kindMap.get(key);
      const row: SummaryGridRow = { label, values };
      if (group !== undefined) row.group = group;
      if (mergeCount !== undefined && mergeCount > 1)
        row.mergeCount = mergeCount;
      if (kind) row.unit = UNIT_KIND_LABEL[kind];
      return row;
    })
    .sort((a, b) => {
      if (a.group && b.group && a.group !== b.group) {
        return a.group.localeCompare(b.group, undefined, {
          sensitivity: 'base',
        });
      }
      return a.label.localeCompare(b.label, undefined, {
        sensitivity: 'base',
      });
    });

/* ═══════════════════════════════════════════════════════════════
   Public API
   ═══════════════════════════════════════════════════════════════ */

/**
 * Compute summary grid rows from an input document's task data.
 *
 * Pure function — no side effects, no hooks, no Redux.
 * Follows the same architectural pattern as `computeGrossMargin.ts`.
 *
 * Reads only the tasks in `categories` — callers pass one category (Plan or
 * Actual) so the same item recorded in both is never added together.
 */
export const computeSummary = (
  inputs: IInput[],
  entityCategory: SummaryEntityCategory,
  dataMetric: SummaryDataMetric,
  temporalLayout: SummaryTemporalLayout,
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): SummaryGridRow[] => {
  if (inputs.length === 0) return [];

  const isMonthly = temporalLayout === SUMMARY_TEMPORAL_MONTHLY;
  const columnCount = isMonthly ? MONTHLY_COLUMN_COUNT : FLAT_COLUMN_COUNT;
  const accumulator = new Map<string, number[]>();
  const groupMap = new Map<string, string>();
  const labelMap = new Map<string, string>();
  const countMap = new Map<string, number>();
  // Physical kind each row settles on (Quantity metric only) — mass rows
  // converge to kg, liquid rows to L, across every row in the table (not
  // just within a row), so the footer total works whenever a category is
  // entirely mass or entirely liquid. A step of the other kind than the
  // row's established kind is skipped rather than blended in.
  const kindMap = new Map<string, UnitKind>();
  // $/ha needs both a cost sum and an area sum per row (per column) to
  // divide at the end — tracked separately from `accumulator` since it
  // isn't itself additive across steps the way Quantity/Area/Cost are.
  const costPerHaCostMap = new Map<string, number[]>();
  const costPerHaAreaMap = new Map<string, number[]>();

  // ── Resolve effective crop/variety per input ─────────────────────
  const resolvedCrops = new Map<string, ResolvedCrop>();
  for (const input of inputs) {
    resolvedCrops.set(input.id, resolveInputCrop(input));
  }

  // ── Pre-create rows for Seed ─────────────────────────────────────
  // Uses resolved variety (from Seed step if present, else input.crop's
  // variety). When no Seed step exists, Total Area falls back to zone
  // area (Quantity and Cost remain 0).
  if (entityCategory === SUMMARY_ENTITY_SEED) {
    for (const input of inputs) {
      // Only plans with a task in the selected category get a row — an
      // Actual-only plan shows nothing under Plan.
      if (!input.tasks.some((task) => categories.has(task.category))) continue;
      const rc = resolvedCrops.get(input.id)!;
      const key = rc.variety;
      const values = getOrCreate(accumulator, key, columnCount);
      groupMap.set(key, rc.crop);
      // No Seed step → fallback Total Area to zone area
      if (!rc.hasSeed && dataMetric === SUMMARY_METRIC_TOTAL_AREA) {
        const zoneArea = input.zone?.zoneArea ?? 0;
        if (zoneArea > 0) {
          const zoneUnit = input.zone?.areaUnit ?? null;
          const areaHa =
            convertToHectares(zoneArea, zoneUnit, gUnits) ?? zoneArea;
          if (areaHa > 0) {
            const monthCol = isMonthly ? getMonthColumn(input.endDate) : 0;
            addValue(values, areaHa, isMonthly, monthCol);
          }
        }
      }
    }
  }

  // ── ALL mode ───────────────────────────────────────────────────
  if (entityCategory === SUMMARY_ENTITY_ALL) {
    if (dataMetric === SUMMARY_METRIC_TOTAL_COST) {
      const revenue = new Array<number>(columnCount).fill(0);
      const cost = new Array<number>(columnCount).fill(0);

      for (const input of inputs) {
        for (const task of input.tasks) {
          if (!categories.has(task.category)) continue;
          for (const step of task.steps) {
            const monthCol = isMonthly
              ? getMonthColumn(step.endDate ?? task.endDate ?? input.endDate)
              : 0;
            const areaHa = resolveAreaHa(step, task, input, gUnits);
            if (areaHa <= 0) continue;

            if (step.stepType === InputStepType.Production) {
              const s = step as IInputStepProduction;
              const val = (s.yield ?? 0) * (s.price ?? 0) * areaHa;
              if (val <= 0) continue;
              addValue(revenue, val, isMonthly, monthCol);
            } else if (COST_STEP_TYPES.has(step.stepType)) {
              const s = step as unknown as IInputStepRateCost;
              const stepCost = s.cost ?? 0;
              const effectiveRate = resolveEffectiveRate(step, task, gUnits);
              if (effectiveRate === null || effectiveRate <= 0 || stepCost <= 0)
                continue;
              const val = effectiveRate * stepCost * areaHa;
              if (val <= 0) continue;
              addValue(cost, val, isMonthly, monthCol);
            }
          }
        }
      }

      return [
        { label: 'Income', values: revenue },
        { label: 'Cost', values: cost },
      ];
    }

    if (dataMetric === SUMMARY_METRIC_COST_PER_HA) {
      // Net $/ha = (total revenue - total cost) / total area, blended
      // across every product type — the same approximation ALL mode
      // already makes for Quantity/Total Area (areas can overlap when
      // multiple products are applied to the same paddock).
      const revenue = new Array<number>(columnCount).fill(0);
      const cost = new Array<number>(columnCount).fill(0);
      const area = new Array<number>(columnCount).fill(0);

      for (const input of inputs) {
        for (const task of input.tasks) {
          if (!categories.has(task.category)) continue;
          for (const step of task.steps) {
            const monthCol = isMonthly
              ? getMonthColumn(step.endDate ?? task.endDate ?? input.endDate)
              : 0;
            const areaHa = resolveAreaHa(step, task, input, gUnits);
            if (areaHa <= 0) continue;

            if (step.stepType === InputStepType.Production) {
              const s = step as IInputStepProduction;
              const val = (s.yield ?? 0) * (s.price ?? 0) * areaHa;
              if (val <= 0) continue;
              addValue(revenue, val, isMonthly, monthCol);
              addValue(area, areaHa, isMonthly, monthCol);
            } else if (COST_STEP_TYPES.has(step.stepType)) {
              const s = step as unknown as IInputStepRateCost;
              const stepCost = s.cost ?? 0;
              const effectiveRate = resolveEffectiveRate(step, task, gUnits);
              if (effectiveRate === null || effectiveRate <= 0 || stepCost <= 0)
                continue;
              const val = effectiveRate * stepCost * areaHa;
              if (val <= 0) continue;
              addValue(cost, val, isMonthly, monthCol);
              addValue(area, areaHa, isMonthly, monthCol);
            }
          }
        }
      }

      const netPerHa = Array.from({ length: columnCount }, (_, ci) =>
        area[ci] > 0 ? (revenue[ci] - cost[ci]) / area[ci] : 0
      );
      return [{ label: 'Total', values: netPerHa }];
    }

    // Quantity or Total Area — single row totalling all steps
    const total = new Array<number>(columnCount).fill(0);

    for (const input of inputs) {
      for (const task of input.tasks) {
        if (!categories.has(task.category)) continue;
        for (const step of task.steps) {
          const monthCol = isMonthly
            ? getMonthColumn(step.endDate ?? task.endDate ?? input.endDate)
            : 0;
          const areaHa = resolveAreaHa(step, task, input, gUnits);
          if (areaHa <= 0) continue;
          let val = 0;

          if (
            dataMetric === SUMMARY_METRIC_QUANTITY &&
            step.stepType === InputStepType.Production
          ) {
            const s = step as IInputStepProduction;
            const raw = (s.yield ?? 0) * areaHa;
            val =
              raw > 0 ? (convertToTonnes(raw, s.yieldUnit, gUnits) ?? raw) : 0;
          } else if (
            dataMetric === SUMMARY_METRIC_QUANTITY &&
            COST_STEP_TYPES.has(step.stepType)
          ) {
            const s = step as unknown as IInputStepRateCost;
            const effectiveRate = resolveEffectiveRate(step, task, gUnits);
            if (effectiveRate === null || effectiveRate <= 0) continue;
            const volume = effectiveRate * areaHa;
            const bulkDensity =
              step.stepType === InputStepType.SoilInput
                ? (step as unknown as IInputStepSoilInput).bulkDensity
                : undefined;
            val =
              convertToTonnes(volume, s.appUnit, gUnits, bulkDensity) ?? volume;
          } else if (dataMetric === SUMMARY_METRIC_TOTAL_AREA) {
            val = areaHa;
          }

          if (val <= 0) continue;
          addValue(total, val, isMonthly, monthCol);
        }
      }
    }

    // Quantity here blends heterogeneous product types (seed, chemical,
    // fertiliser, production) into one figure — tonnes is the one place
    // that forced normalisation is unavoidable, so label it explicitly.
    const row: SummaryGridRow = { label: 'Total', values: total };
    if (dataMetric === SUMMARY_METRIC_QUANTITY) row.unit = 't';
    return [row];
  }

  // ── General case: iterate inputs → tasks → steps ───────────────
  for (const input of inputs) {
    const resolved = resolvedCrops.get(input.id)!;

    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;
      for (const step of task.steps) {
        const monthCol = isMonthly
          ? getMonthColumn(step.endDate ?? task.endDate ?? input.endDate)
          : 0;
        const key = getEntityKey(entityCategory, step, resolved);
        if (key === null) continue;

        if (
          entityCategory !== SUMMARY_ENTITY_SEED &&
          step.item &&
          !labelMap.has(key)
        ) {
          labelMap.set(key, step.item);
        }

        // Seed: set group (crop name)
        if (entityCategory === SUMMARY_ENTITY_SEED && !groupMap.has(key)) {
          if (step.stepType === InputStepType.Seed) {
            const s = step as unknown as IInputStepSeed;
            groupMap.set(key, s.item || resolved.crop);
          } else {
            groupMap.set(key, resolved.crop);
          }
        }

        // Always ensure the row exists even if value is 0
        // (e.g. rate=0 operations, zero-cost items still appear as rows)
        getOrCreate(accumulator, key, columnCount);

        const areaHa = resolveAreaHa(step, task, input, gUnits);

        let value: number;
        if (dataMetric === SUMMARY_METRIC_COST_PER_HA) {
          const stepCost = calculateCost(step, task, areaHa, gUnits);
          if (stepCost === 0 || areaHa <= 0) continue;
          countMap.set(key, (countMap.get(key) ?? 0) + 1);
          addValue(
            getOrCreate(costPerHaCostMap, key, columnCount),
            stepCost,
            isMonthly,
            monthCol
          );
          addValue(
            getOrCreate(costPerHaAreaMap, key, columnCount),
            areaHa,
            isMonthly,
            monthCol
          );
          continue;
        } else if (dataMetric === SUMMARY_METRIC_QUANTITY) {
          const raw = calculateQuantityWithUnit(
            entityCategory,
            step,
            task,
            areaHa,
            gUnits
          );
          if (raw === null) continue;

          const kind = resolveUnitKind(raw.unit, gUnits);
          if (kind === null) continue; // unrecognised unit — nothing to normalise to

          const converted =
            kind === 'mass'
              ? convertToKg(raw.value, raw.unit, gUnits)
              : convertToLitres(raw.value, raw.unit, gUnits);
          if (converted === null) continue;

          const established = kindMap.get(key);
          if (!established) {
            kindMap.set(key, kind);
          } else if (established !== kind) {
            // Row already settled on the other kind (rare — same product
            // recorded as both mass and liquid across steps). Can't merge
            // without a bulk-density assumption, so skip this contribution.
            continue;
          }
          value = converted;
        } else {
          value = calculateMetric(dataMetric, step, task, areaHa, gUnits);
        }
        if (value === 0) continue;

        countMap.set(key, (countMap.get(key) ?? 0) + 1);

        const values = getOrCreate(accumulator, key, columnCount);
        addValue(values, value, isMonthly, monthCol);
      }
    }
  }

  // $/ha rows were accumulated as separate cost/area sums — resolve the
  // final ratio into `accumulator` now that every step has been seen.
  if (dataMetric === SUMMARY_METRIC_COST_PER_HA) {
    Array.from(costPerHaCostMap.entries()).forEach(([key, costValues]) => {
      const areaValues = costPerHaAreaMap.get(key);
      const ratioValues = costValues.map((c, i) => {
        const a = areaValues?.[i] ?? 0;
        return a > 0 ? c / a : 0;
      });
      accumulator.set(key, ratioValues);
    });
  }

  return toSortedRows(accumulator, groupMap, labelMap, countMap, kindMap);
};
